import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Phone, Copy, AlertTriangle, Pill, Cpu } from "lucide-react";
import { PetAvatar } from "@/components/PetAvatar";
import { api } from "@/lib/api";
import { VET_LINE } from "@/lib/constants";

function Card({ pet, h }) {
  const meds = pet.schedules.filter((s) => s.type === "medication");
  const vetPhone = pet.vet_phone || h.vet_phone;
  const copy = () => { navigator.clipboard.writeText(pet.microchip); toast.success("Microchip number copied"); };
  return (
    <div className="pl-card border-[3px] border-[#E07A5F] p-5 mb-5" data-testid={`emergency-card-${pet.id}`}>
      <div className="flex items-center gap-4">
        <PetAvatar pet={pet} size={80} ring />
        <div><h2 className="font-heading text-3xl font-extrabold">{pet.name}</h2>
          <p className="text-slate-500 capitalize">{[pet.species, pet.breed, pet.weight].filter(Boolean).join(" · ")}</p></div>
      </div>
      <div className="mt-4 space-y-2 text-sm">
        <p className="rounded-xl bg-[#FDF2F0] px-3 py-2 flex gap-2"><AlertTriangle className="h-4 w-4 text-[#C2410C] mt-0.5" /><span><b>Allergies:</b> {pet.allergies || "None entered"}</span></p>
        {pet.conditions && <p className="rounded-xl bg-[#FAF7F2] px-3 py-2"><b>Conditions:</b> {pet.conditions}</p>}
        <div className="rounded-xl bg-[#FAF7F2] px-3 py-2 flex gap-2"><Pill className="h-4 w-4 mt-0.5" />
          <div><b>Medications:</b> {pet.medications_note || (meds.length ? "" : "None entered")}
            {meds.map((m) => <div key={m.id}>{m.title}{m.amount ? ` · ${m.amount}` : ""} ({(m.times || []).join(", ") || "anytime"})</div>)}</div></div>
        {pet.microchip && (
          <button onClick={copy} data-testid={`emergency-microchip-copy-${pet.id}`} className="w-full rounded-xl bg-[#FAF7F2] px-3 py-2 flex items-center gap-2 text-left">
            <Cpu className="h-4 w-4" /><span className="flex-1"><b>Microchip:</b> <span className="font-mono">{pet.microchip}</span></span><Copy className="h-4 w-4 text-slate-400" /></button>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
        {vetPhone ? <a href={`tel:${vetPhone}`} data-testid={`emergency-call-vet-${pet.id}`} className="pl-btn-primary"><Phone className="h-4 w-4" />Call vet {pet.vet_name || h.vet_name || ""}</a>
          : <p className="text-sm text-slate-500 self-center">Add your vet phone in Settings.</p>}
        {h.emergency_phone && <a href={`tel:${h.emergency_phone}`} data-testid={`emergency-call-contact-${pet.id}`} className="pl-btn-ghost"><Phone className="h-4 w-4" />{h.emergency_name || "Emergency contact"}</a>}
        <a href="tel:8884264435" className="pl-btn-ghost sm:col-span-2 text-sm" data-testid={`emergency-poison-${pet.id}`}><Phone className="h-4 w-4" />ASPCA Animal Poison Control (888) 426-4435</a>
      </div>
    </div>
  );
}

export default function EmergencyCard() {
  const [params] = useSearchParams();
  const [guide, setGuide] = useState(null);
  useEffect(() => { api.get("/guide").then((r) => setGuide(r.data)); }, []);
  if (!guide) return <div className="pl-card h-60 animate-pulse" />;
  const only = params.get("pet");
  const pets = only ? guide.pets.filter((p) => p.id === only) : guide.pets;
  return (
    <div data-testid="emergency-page">
      <h1 className="font-heading text-4xl font-extrabold mb-1">Emergency</h1>
      <p className="text-sm text-slate-500 mb-5">The key facts, one tap away. Poison Control may charge a consultation fee.</p>
      {pets.map((p) => <Card key={p.id} pet={p} h={guide.household} />)}
      {pets.length === 0 && <p className="text-slate-500">Add a pet first.</p>}
      <p className="text-[11px] text-slate-400">{VET_LINE}</p>
    </div>
  );
}
